import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { RentalAsset } from '@kaltura-ott/tvpil-shared/lib/rhino/data/rental/types';

import { useMouseEnterHandle } from 'components/presenters/Card/hooks/useMouseEnterHandle/useMouseEnterHandle';
import { useMouseLeaveHandle } from 'components/presenters/Card/hooks/useMouseLeaveHandle/useMouseLeaveHandle';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider/RailProvider';
import { useWidgetContext } from 'context/widgetProvider/widgetProvider';
import { useParentalPinStore, useRecordRepository } from 'hooks';
import routes from 'routesPaths';
import { getAriaLabelForAsset } from 'utils';

import { useClickHandler } from '../useClickHandler/useClickHandler';
import { useHandleClickInfo } from '../useHandleClickInfo';
import { useRentalCardRating } from '../useRentalCardRating/useRentalCardRating';
import { useRentalsCardImages } from '../useRentalsCardImages';
import { useRentalsCardRef } from '../useRentalsCardRef';

interface Props {
  rentalItem: RentalAsset;
  itemWidth: number;
  imageHeight: number;
}

function useRentalsCard({ rentalItem, itemWidth, imageHeight }: Props) {
  const intl = useIntl();
  const parentalPinStore = useParentalPinStore();
  const link = routes.vod.link(rentalItem.id, rentalItem.type);
  // using version v2 of the rail provider
  const { onHover, nextButtons } = useRailContext();
  const {
    settings,
    isGrid,
    settings: { shouldShowRating },
    imageWidth, // should be removed after fallback params becomes required
  } = useWidgetContext();

  // adults
  const isAdult = !!rentalItem?.isAdult;

  const isAdultPinNotEntered = !parentalPinStore.isAdultPinEntered;
  const isRestricted = !!(isAdult && isAdultPinNotEntered);

  // refs
  const handleLinkRef = useRef(null);
  const { ref, handleRef } = useRentalsCardRef(itemWidth, imageHeight);
  // handlers
  const onMouseEnter = useMouseEnterHandle({ ref, itemWidth, imageHeight, isGrid, onHover, nextButtons });
  const onMouseLeave = useMouseLeaveHandle(onHover);
  const clickHandler = useClickHandler({
    parentalPinStore,
    data: rentalItem,
    isChannelHasNoProgram: false,
    mediaTypeParam: rentalItem.type,
    isPurchasable: false,
    isRestricted,
    imageHeight,
    link,
  });
  const { handleClickInfo } = useHandleClickInfo({
    id: rentalItem?.id!,
    externalId: undefined,
    mediaTypeParam: rentalItem.type,
    isChannelHasNoProgram: false,
  });
  // rating
  const { isDisplayRating, assetRating } = useRentalCardRating({ shouldShowRating, rentalItem });
  // images urls
  const { backgroundImageUrl, defaultImageUrl } = useRentalsCardImages({
    rentalItem,
    imageType: settings.imageType,
    imageWidth,
    imageHeight,
  });

  // For record indication
  const recordRepository = useRecordRepository();
  const record = rentalItem?.id ? recordRepository.records.get(rentalItem.id.toString()) : null;
  const isRecord = Boolean(record);
  const isSeriesRecord = false; // Need to check
  //

  // getting aria-label text
  const ariaLabel = getAriaLabelForAsset({
    intl,
    title: rentalItem.title,
    isRestricted,
    isRecord,
    isSeriesRecord,
    shouldPronouncedAiringTime: false
  });

  return {
    link,
    title: rentalItem.title,
    isAdult,
    isRecord,
    ariaLabel,
    handleRef,
    assetRating,
    clickHandler,
    onMouseEnter,
    onMouseLeave,
    handleLinkRef,
    isSeriesRecord,
    handleClickInfo,
    isDisplayRating,
    defaultImageUrl,
    backgroundImageUrl,
  };
}

export { useRentalsCard };
